import React, { useEffect, useMemo } from 'react';
import { Alert, App, Button, Flex, Form, Input, Modal, Tag, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  AuditOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FlagOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  ActiveChangeRequestSummary,
  Appointment,
  AppointmentRescheduleRequest
} from '@/api/mcp-appointments';
import {
  useApproveClientRescheduleRequestMutation,
  useRejectClientRescheduleRequestMutation
} from '@/api/mcp-appointments/mutations';
import { useClientCustomerDisplayMap } from '@/hooks/useClientCustomerDisplayMap';
import { getAppointmentClientDisplayLabel } from '../appointmentClientDisplay';
import {
  formatAppointmentDateTime,
  formatAppointmentTimeRange,
  getCalendarAppointmentHeadline,
  getChangeRequestStatusLabel,
  getStatusTagColor
} from '../calendar/calendarUtils';
import { getApiErrorMessage } from '@/utils/apiError';
import { AppointmentDetailRow } from './AppointmentDetailRow';
import { AppointmentDetailsBox } from './AppointmentDetailsBox';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { AppointmentDetailSectionLabel } from './AppointmentDetailSectionLabel';
import { formModalWidth } from '@/components/ui/formModalStyles';

const { Text, Paragraph } = Typography;

export type RescheduleChangeRequestReview =
  | AppointmentRescheduleRequest
  | (ActiveChangeRequestSummary & { type: 'RESCHEDULE' });

/**
 * Narrows an appointment's active change request to a reschedule request reviewable by this modal.
 * @param request - The appointment's `activeChangeRequest`, if any
 * @returns True when the request exists and is of type `RESCHEDULE`
 */
export function isRescheduleChangeRequest(
  request: ActiveChangeRequestSummary | undefined
): request is ActiveChangeRequestSummary & { type: 'RESCHEDULE' } {
  return request !== undefined && request.type === 'RESCHEDULE';
}

interface AppointmentChangeRequestReviewModalProps {
  open: boolean;
  onClose: () => void;
  request: RescheduleChangeRequestReview | null;
  appointment?: Appointment | null;
  customerLabel?: string | null;
}

interface OriginalTimeRange {
  start: string;
  end?: string | null;
}

function getOriginalTimeRange(
  request: RescheduleChangeRequestReview,
  appointment?: Appointment | null
): OriginalTimeRange {
  if (appointment?.startTime) {
    return { start: appointment.startTime, end: appointment.endTime };
  }
  if ('currentStartTime' in request) {
    return { start: request.currentStartTime, end: request.currentEndTime };
  }
  return { start: '' };
}

function getRequestedTimeRange(request: RescheduleChangeRequestReview): OriginalTimeRange {
  const start = 'requestedStartTime' in request ? request.requestedStartTime : undefined;
  const end = 'requestedEndTime' in request ? request.requestedEndTime : undefined;
  return { start: start ?? '', end };
}

export const AppointmentChangeRequestReviewModal: React.FC<AppointmentChangeRequestReviewModalProps> = ({
  open,
  onClose,
  request,
  appointment = null,
  customerLabel
}) => {
  const { t, i18n } = useTranslation('manage');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { userDisplayById } = useClientCustomerDisplayMap(open);

  const approveReschedule = useApproveClientRescheduleRequestMutation();
  const rejectReschedule = useRejectClientRescheduleRequestMutation();

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const resolvedCustomerLabel = useMemo(() => {
    if (customerLabel?.trim()) {
      return customerLabel.trim();
    }
    if (appointment) {
      return getAppointmentClientDisplayLabel(
        appointment,
        t('appointmentComponents.details.anonymous'),
        userDisplayById
      );
    }
    return t('appointmentComponents.details.anonymous');
  }, [appointment, customerLabel, t, userDisplayById]);

  const appointmentTitle = useMemo(() => {
    if (!appointment) {
      return null;
    }
    return getCalendarAppointmentHeadline(appointment);
  }, [appointment]);

  if (!request) {
    return null;
  }

  const originalTime = getOriginalTimeRange(request, appointment);
  const requestedTime = getRequestedTimeRange(request);
  const isPending = request.status === 'PENDING';
  const isLoading = approveReschedule.isPending || rejectReschedule.isPending;
  const statusLabel = getChangeRequestStatusLabel(request.status, t);
  const showTimeComparison = Boolean(originalTime.start || requestedTime.start);
  const subtitle = originalTime.start
    ? `${resolvedCustomerLabel} · ${formatAppointmentDateTime(originalTime.start, i18n.language)}`
    : resolvedCustomerLabel;

  const handleApprove = async () => {
    try {
      await approveReschedule.mutateAsync({ requestId: request.id });
      message.success(t('appointmentComponents.changeRequest.approvedSuccess'));
      onClose();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('appointmentComponents.changeRequest.approveFailed')));
    }
  };

  const handleReject = async () => {
    try {
      const values = await form.validateFields();
      const body = values.resolutionNote?.trim() ? { resolutionNote: values.resolutionNote.trim() } : undefined;
      await rejectReschedule.mutateAsync({ requestId: request.id, body });
      message.success(t('appointmentComponents.changeRequest.rejectedSuccess'));
      onClose();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('appointmentComponents.changeRequest.rejectFailed')));
    }
  };

  const footer = isPending ? (
    <Flex justify="flex-end" gap={token.marginXS} wrap="wrap">
      <Button onClick={onClose} disabled={isLoading}>
        {t('common:actions.cancel', { defaultValue: 'Cancel' })}
      </Button>
      <Button danger onClick={() => void handleReject()} loading={isLoading}>
        {t('appointmentComponents.changeRequest.reject')}
      </Button>
      <Button type="primary" onClick={() => void handleApprove()} loading={isLoading}>
        {t('appointmentComponents.changeRequest.approve')}
      </Button>
    </Flex>
  ) : (
    <Button onClick={onClose}>{t('common:actions.close', { defaultValue: 'Close' })}</Button>
  );

  return (
    <Modal
      title={
        <ModalTitle
          icon={<AuditOutlined />}
          title={t('appointmentComponents.changeRequest.reviewTitle')}
          subtitle={subtitle}
        />
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={formModalWidth(560)}
      centered
      destroyOnHidden
    >
      <Flex vertical gap={token.marginMD} style={{ paddingTop: token.paddingXS }}>
        <AppointmentDetailsBox>
          <AppointmentDetailRow icon={<UserOutlined />} label={t('appointmentComponents.changeRequest.customer')}>
            <Text strong>{resolvedCustomerLabel}</Text>
          </AppointmentDetailRow>
          {appointmentTitle ? (
            <AppointmentDetailRow
              icon={<FileTextOutlined />}
              label={t('appointmentComponents.changeRequest.appointment')}
            >
              <Text>{appointmentTitle}</Text>
            </AppointmentDetailRow>
          ) : null}
          <AppointmentDetailRow icon={<FlagOutlined />} label={t('appointmentComponents.changeRequest.status')}>
            <Tag color={getStatusTagColor(request.status)} style={{ margin: 0 }}>
              {statusLabel}
            </Tag>
          </AppointmentDetailRow>
        </AppointmentDetailsBox>

        {showTimeComparison ? (
          <div>
            <AppointmentDetailSectionLabel>{t('appointmentComponents.changeRequest.typeReschedule')}</AppointmentDetailSectionLabel>
            <AppointmentDetailsBox>
              {originalTime.start ? (
                <AppointmentDetailRow
                  icon={<ClockCircleOutlined />}
                  label={t('appointmentComponents.changeRequest.originalTime')}
                >
                  <Text type="secondary" delete>
                    {formatAppointmentTimeRange(originalTime.start, originalTime.end, i18n.language)}
                  </Text>
                </AppointmentDetailRow>
              ) : null}
              {requestedTime.start ? (
                <AppointmentDetailRow
                  icon={<ArrowRightOutlined />}
                  label={t('appointmentComponents.changeRequest.requestedTime')}
                >
                  <Text strong style={{ color: token.colorPrimary }}>
                    {formatAppointmentTimeRange(requestedTime.start, requestedTime.end, i18n.language)}
                  </Text>
                </AppointmentDetailRow>
              ) : null}
            </AppointmentDetailsBox>
          </div>
        ) : null}

        {request.reason ? (
          <div>
            <AppointmentDetailSectionLabel>{t('appointmentComponents.changeRequest.customerNote')}</AppointmentDetailSectionLabel>
            <AppointmentDetailsBox filled>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {request.reason}
              </Paragraph>
            </AppointmentDetailsBox>
          </div>
        ) : null}

        {!isPending ? (
          <Alert type="info" showIcon title={t('appointmentComponents.changeRequest.alreadyResolved')} />
        ) : null}

        {isPending ? (
          <Form form={form} layout="vertical">
            <Form.Item
              name="resolutionNote"
              label={t('appointmentComponents.changeRequest.internalRejectNote')}
              extra={t('appointmentComponents.changeRequest.internalRejectNoteHint')}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={3}
                maxLength={2000}
                placeholder={t('appointmentComponents.changeRequest.internalRejectNotePlaceholder')}
              />
            </Form.Item>
          </Form>
        ) : null}
      </Flex>
    </Modal>
  );
};
